import React, { useEffect, useState } from "react";
import "../FormTemplates.css";
import w4bg from "./W4-template.jpg";
import OverlayField from "../OverlayField";
import { CHILD_CREDIT, OTHER_DEPENDENT_CREDIT } from "../../Mobile/DetailsFillingSections/W4Details/w4Credits";

/**
 * Form W-4 (2026), page 1.
 *
 * The background is the IRS's own page 1, rendered at 2550 x 3300. Every value is placed from
 * the form's own field boxes: `[left, bottom, right, top]` in PDF points on a 612 x 792 page,
 * read straight from the fillable PDF (so a new tax year is: render the new page 1, read its
 * field boxes, replace the numbers below). The signature and date have no field in the PDF; they
 * sit on the Step 5 lines, measured from the rendered page.
 */
const PAGE_WIDTH = 611.976;
const PAGE_HEIGHT = 791.968;

const FIELDS = {
  firstName: [95, 684, 273, 698],
  lastName: [275, 684, 474, 698],
  ssn: [476, 684, 576, 698],
  address: [95, 660, 474, 674],
  cityStateZip: [95, 636, 474, 650],
  filingSingle: [115, 626, 123, 634],
  filingJoint: [115, 614, 123, 622],
  filingHead: [115, 602, 123, 610],
  twoJobs: [564, 380, 572, 388],
  children: [418, 300, 482, 312],
  otherDependents: [418, 288, 482, 300],
  credits: [511, 264, 576, 276],
  otherIncome: [511, 228, 576, 240],
  deductions: [511, 192, 576, 204],
  extraWithholding: [511, 174, 576, 186],
  employer: [95, 36, 388, 66],
  signature: [101, 91, 446, 113],
  date: [461, 91, 576, 103],
};

const DEFAULT_EMPLOYER = "Freeman, International Plaza #2, 14221 Dallas Pkwy Suite 200, Dallas, TX 75254, USA";

// The template is drawn 794px wide when downloaded and 359px wide in the mobile preview.
const DOWNLOAD_WIDTH = 794;
const PREVIEW_WIDTH = 359;

/** A field's box, in pixels, at the download or preview width. */
const boxFor = (rect, isDownload) => {
  const scale = (isDownload ? DOWNLOAD_WIDTH : PREVIEW_WIDTH) / PAGE_WIDTH;
  const [left, bottom, right, top] = rect;
  return {
    left: left * scale,
    top: (PAGE_HEIGHT - top) * scale,
    width: (right - left) * scale,
    height: (top - bottom) * scale,
  };
};

const number = (value) => {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** Whole dollars with thousands separators, cents only when there are some; blank for zero. */
const money = (n) => {
  if (!(n > 0)) return "";
  return Number.isInteger(n) ? n.toLocaleString("en-US") : n.toFixed(2);
};

const filingBox = (filingStatus) => {
  const raw = String(filingStatus || "").trim();
  if (
    raw === "Single or Married, filing separately" ||
    raw === "I'm single" ||
    raw === "I’m single" ||
    raw === "I'm married and filing separately" ||
    raw === "I’m married and filing separately" ||
    raw === "SINGLE_OR_MARRIED_SEPARATE"
  ) return FIELDS.filingSingle;
  if (
    raw === "Married filing jointly or Qualifying surviving spouse" ||
    raw === "I'm married and filing together" ||
    raw === "I’m married and filing together" ||
    raw === "MARRIED_JOINT"
  ) return FIELDS.filingJoint;
  return FIELDS.filingHead;
};

export default function W4Form({ data, isDownload = false }) {
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = w4bg;
    img.onload = () => setBgLoaded(true);
  }, []);

  // Base font sizes: a one-line field box is 14pt tall, so these leave the text a little room.
  const baseFont = isDownload ? 12 : 6.5;
  const field = (name) => ({ d: boxFor(FIELDS[name], true), p: boxFor(FIELDS[name], false) });
  const text = (name, value) => {
    const { d, p } = field(name);
    return (
      <OverlayField
        isDownload={isDownload}
        d={{ ...d, fontSize: baseFont }}
        p={{ ...p, fontSize: baseFont }}
      >
        {value}
      </OverlayField>
    );
  };
  const tick = (rect) => {
    const box = boxFor(rect, isDownload);
    return (
      <div
        className="overlay"
        style={{
          ...box,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: isDownload ? 13 : 6.5,
          lineHeight: 1,
        }}
      >
        ✓
      </div>
    );
  };

  /* ---- Step 1 ---- */
  const middleInitial = data?.middleInitial || String(data?.middleName || "").trim().charAt(0).toUpperCase();
  const firstName = [data?.firstName, middleInitial].filter(Boolean).join(" ");

  const street = String(data?.address || "").trim();
  const apartment = String(data?.apartNumber || "").trim();
  const address = apartment && !/apt/i.test(street) ? `${street}, Apt ${apartment}` : street;
  const cityStateZip = `${[data?.city, data?.state].filter(Boolean).join(", ")} ${data?.zipCode || ""}`.trim();

  const ssnRaw = String(data?.ssnNumber || data?.ssn || data?.ssnMasked || "").trim();
  const ssnDigits = ssnRaw.replace(/\D/g, "");
  const ssn = ssnDigits.length >= 4 ? `XXX-XX-${ssnDigits.slice(-4)}` : "";

  /* ---- Steps 2 to 4 ---- */
  const twoJobs = Boolean(data?.hasTwoJobs) || data?.jobsCount === "TWO";

  const aboveLimit = data?.incomeAboveLimit === true;
  const hasCounts = data?.qualifyingChildren !== undefined || data?.otherDependents !== undefined;
  const childrenAmount = aboveLimit ? 0 : number(data?.qualifyingChildren) * CHILD_CREDIT;
  const otherAmount = aboveLimit ? 0 : number(data?.otherDependents) * OTHER_DEPENDENT_CREDIT;
  // Sessions saved before the two counts existed only have the total.
  const creditsTotal = aboveLimit
    ? 0
    : hasCounts
      ? childrenAmount + otherAmount
      : number(data?.dependentCredits ?? data?.taxCredit);

  // 4(c) is the employee's own extra amount plus the multiple-jobs worksheet/estimator amount.
  const multipleJobsAmount = data?.jobsCount === "MORE_THAN_TWO" ? number(data?.step2Amount) : 0;
  const extraWithholding = number(data?.extraWithholding) + multipleJobsAmount;

  /* ---- Step 5 and employer ---- */
  const signedOn = data?.signedAt ? new Date(data.signedAt) : new Date();
  const signedDate = Number.isNaN(signedOn.getTime()) ? "" : signedOn.toLocaleDateString("en-US");
  const employer = data?.employerNameAndAddress || DEFAULT_EMPLOYER;

  const signatureBox = boxFor(FIELDS.signature, isDownload);

  return (
    <div className={isDownload ? "w4-wrapper-download" : "w4-wrapper-preview"}>
      {bgLoaded && (<>
        <img src={w4bg} alt="W4 Form" className="w4-bg" />

        {text("firstName", firstName)}
        {text("lastName", data?.lastName || "")}
        {text("ssn", ssn)}
        {text("address", address)}
        {text("cityStateZip", cityStateZip)}

        {data?.filingStatus && tick(filingBox(data.filingStatus))}
        {twoJobs && tick(FIELDS.twoJobs)}

        {text("children", money(childrenAmount))}
        {text("otherDependents", money(otherAmount))}
        {text("credits", money(creditsTotal))}
        {text("otherIncome", money(number(data?.otherIncome)))}
        {text("deductions", money(number(data?.totalDeductions ?? data?.deductions)))}
        {text("extraWithholding", money(extraWithholding))}

        {data?.signatureBase64 && (
          <div
            className="overlay"
            style={{
              ...signatureBox,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              overflow: "hidden",
            }}
          >
            <img
              src={`${data.signatureBase64}`}
              alt="signature"
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
            />
          </div>
        )}
        {text("date", signedDate)}
        {text("employer", employer)}
      </>)}
    </div>
  );
}
